import { type AxiosPromise } from "axios";
import React from "react";
import { Button, Message } from "semantic-ui-react";
import { type FileTypes } from "shared/utils/fileutils";
import { NoDataAvailableDiv } from "../NoDataAvailableDiv";
import { Loading } from "../panels";
import { validateExtension, validateFileLength } from "utilities/fileUploadUtils";
import { Checkbox } from "components/Atoms/inputs/Checkbox";

interface Props {
  relatedFilesApi: () => AxiosPromise<server.dto.ApiResponseWrapper<server.dto.RelatedFiles>>
  onFilesSelected: (files: server.dto.File[]) => void
  initialSelectedFiles: server.dto.File[]
  relatedFiles: server.dto.RelatedFiles
  onRelatedFilesChanged: (relatedFiles: server.dto.RelatedFiles) => void

  error?: string

  maxFileSize?: number
  validFileTypes: FileTypes
}

interface State {
  selectedFiles: server.dto.File[]
  tooLargeError: string
  invalidFileTypeError: string

  loading: boolean
}

class RelatedFiles extends React.Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    selectedFiles: this.props.initialSelectedFiles,
    tooLargeError: undefined,
    invalidFileTypeError: undefined,
    loading: true
  };

  componentDidUpdate(_, prevState: State) {
    if (prevState.selectedFiles.length !== this.state.selectedFiles.length) {
      for (const file of this.state.selectedFiles) {
        const extensionValid = validateExtension([file], this.props.validFileTypes);

        if (extensionValid !== true) {
          this.setState({ invalidFileTypeError: extensionValid });
          return;
        }

        if (this.props.maxFileSize != null) {
          const fileLengthValid = validateFileLength(file.contentLength, this.props.maxFileSize);
          if (fileLengthValid !== true) {
            this.setState({ tooLargeError: fileLengthValid });
            return;
          }
        }
      }

      this.setState({ invalidFileTypeError: undefined, tooLargeError: undefined });
    }
  }

  onFileToggled = (file: server.dto.File, selectedFilesSet: Set<number>) => {
    if (selectedFilesSet.has(file.id)) {
      const newFiles = this.state.selectedFiles.filter(f => f.id !== file.id);
      this.setState({ selectedFiles: newFiles });
    } else {
      this.setState({ selectedFiles: [...this.state.selectedFiles, file] });
    }
  };

  componentDidMount() {
    this.props.relatedFilesApi().then(res => {
      this.props.onRelatedFilesChanged(res.data.data);
      this.setState({ loading: false });
    });
  }

  public render(): JSX.Element {
    const { selectedFiles, loading } = this.state;
    const { relatedFiles } = this.props;
    const selectedFilesSet = new Set(selectedFiles.map(f => f.id));

    const error = this.props.error ?? this.state.invalidFileTypeError ?? this.state.tooLargeError;
    const hasError = !!error;
    const cantSave = !!(this.state.invalidFileTypeError ?? this.state.tooLargeError);

    if (loading) {
      return <div className="grow1 flex justify-center align-center">
        <Loading />
      </div>;
    }

    const noItems = relatedFiles?.relatedItems != null &&
      relatedFiles.relatedItems.length === 0 &&
      relatedFiles.directItems != null &&
      relatedFiles.directItems.length === 0;
    return <>
      <div className="grow1 scrollbox-auto flex-column gap-larger">
        <div className="flex-column gap-small">
          {relatedFiles?.directItems.map(item => (
            <div key={item.id}>
              <Checkbox
                checked={selectedFilesSet.has(item.id)}
                onChange={() => { this.onFileToggled(item, selectedFilesSet); }}
                label={`${item.fullName} (${item.contentLengthText})`}
              />
            </div>
          ))}
        </div>

        {relatedFiles?.relatedItems.map((relatedItem, idx) => <div key={relatedItem.title + idx} className="flex-column gap-small">
          <div>
            {relatedItem.title}
          </div>
          <div style={{ marginLeft: 5 }} className="flex-column gap-small" >
            {relatedItem.items.map(item => (
              <div key={item.id}>
                <Checkbox
                  checked={selectedFilesSet.has(item.id)}
                  onChange={() => { this.onFileToggled(item, selectedFilesSet); }}
                  label={`${item.fullName} (${item.contentLengthText})`}
                />
              </div>
            ))}
          </div>
        </div>)}

        {noItems && <NoDataAvailableDiv />}
      </div>

      {hasError && <Message className="margin-top" style={{ minHeight: "3em" }} color="red">
        {error}
      </Message>}

      {this.props.children}

      <div className="flex">
        <div className="grow1"></div>
        <Button disabled={cantSave} primary onClick={() => { this.props.onFilesSelected(selectedFiles); }}>{RESX.Buttons.BtnOk}</Button>
      </div>
    </>;
  }
}

export default RelatedFiles;